"use client";

import { useEffect, useRef } from "react";

import { GoogleScriptLoader } from "@/components/auth/google-script-loader";

import { loginGoogleOAuth } from "@/lib/auth/client";
import { destinationAfterPasswordLogin } from "@/lib/auth/return-to";

interface GoogleResponse {
  credential: string;
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: GoogleResponse) => void;
          }) => void;
          renderButton: (
            element: HTMLElement,
            config: { theme: string; size: string; width?: string },
          ) => void;
        };
      };
    };
  }
}

export function GoogleSignInButton({
  handleError,
}: {
  handleError: (error: string) => void;
}) {
  const buttonRef = useRef<HTMLDivElement>(null);
  const initialized = useRef(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: ignored using `--suppress`
  useEffect(() => {
    if (initialized.current) return;

    const initializeGoogleSignIn = () => {
      if (!window.google || !buttonRef.current) return;

      window.google?.accounts.id.initialize({
        client_id: process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID!,
        callback: async (response: { credential?: string }) => {
          try {
            if (response.credential) {
              await loginGoogleOAuth(response.credential);

              const params = new URLSearchParams(window.location.search);
              window.location.href = destinationAfterPasswordLogin(
                params.get("returnTo"),
                "/",
              );
            }
          } catch (error: unknown) {
            handleError(
              error instanceof Error
                ? error.message
                : "An unknown error occurred",
            );
          }
        },
      });

      const buttonConfig: { theme: string; size: string; width?: string } = {
        theme: "outline",
        size: "large",
      };

      // Google only accepts a pixel width, not percents.
      // https://developers.google.com/identity/gsi/web/reference/js-reference
      const width = Math.floor(buttonRef.current.offsetWidth);
      if (width > 0) {
        buttonConfig.width = String(width);
      }

      window.google?.accounts.id.renderButton(buttonRef.current, buttonConfig);

      initialized.current = true;
    };

    // Try to initialize immediately if Google is already loaded
    initializeGoogleSignIn();

    // Also set up the event listener for when Google loads
    window.addEventListener("googleLoaded", initializeGoogleSignIn);

    return () => {
      window.removeEventListener("googleLoaded", initializeGoogleSignIn);
    };
  }, []);

  // Large GSI button is 40px; the iframe is 150px until it loads.
  // Use 44px so overflow-hidden doesn't clip the 1px outline border.
  return (
    <>
      <GoogleScriptLoader />
      <div ref={buttonRef} className="h-11 w-full overflow-hidden" />
    </>
  );
}
